// src/pages/DailyBranchSales.tsx
import { useEffect, useState } from "react";
import api from "../api/axios";
import PageLayout from "../components/layout/PageLayout";
import useAuth from "../auth/useAuth";
import {
  BanknotesIcon,
  ChartBarIcon,
  BuildingStorefrontIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline";

interface BranchSales {
  branch_name: string;
  total_sales: string;
}

interface DailySalesResponse {
  date: string;
  total_sales_all_branches: string;
  total_return_sales_all_branches: string;
  currency_name: string;
  currency_symbol: string;
  branches: BranchSales[];
}

interface BranchTransactions {
  branch_name: string;
  total_receipts: string;
  total_payments: string;
}

interface DailyTransactionsResponse {
  date: string;
  total_receipts_all_branches: string;
  total_payments_all_branches: string;
  currency_name: string;
  currency_symbol: string;
  branches: BranchTransactions[];
}

const formatNumber = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export default function DailyBranchSales() {
  const { data: auth } = useAuth();
  const [data, setData] = useState<DailySalesResponse | null>(null);
  const [txnData, setTxnData] = useState<DailyTransactionsResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [salesRes, txnsRes] = await Promise.all([
          api.get<DailySalesResponse>("/shop/daily-branch-sales/"),
          api.get<DailyTransactionsResponse>(
            "/shop/daily-branch-transactions/",
          ),
        ]);

        setData(salesRes.data);
        setTxnData(txnsRes.data);
        setError(null);
      } catch (err) {
        console.error(err);
        setError(
          "Failed to fetch daily sales/transactions. Please try again later.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalSalesAll = data ? parseFloat(data.total_sales_all_branches) : 0;

  const totalReturnSalesAll = data
    ? parseFloat(data.total_return_sales_all_branches)
    : 0;

  const netSalesAll = totalSalesAll - totalReturnSalesAll;

  const salesCurrencyName = data?.currency_name || "";
  const salesCurrencySymbol = data?.currency_symbol || "";

  const sortedBranches = data
    ? [...data.branches].sort((a, b) => {
        const netA =
          parseFloat(a.total_sales) -
          parseFloat((a as any).total_return_sales ?? "0");
        const netB =
          parseFloat(b.total_sales) -
          parseFloat((b as any).total_return_sales ?? "0");
        return netB - netA;
      })
    : [];

  const totalReceiptsAll = txnData
    ? parseFloat(txnData.total_receipts_all_branches)
    : 0;

  const totalPaymentsAll = txnData
    ? parseFloat(txnData.total_payments_all_branches)
    : 0;

  const netCashAll = totalReceiptsAll - totalPaymentsAll;

  const txnCurrencyName = txnData?.currency_name || "";
  const txnCurrencySymbol = txnData?.currency_symbol || "";

  const txnBranchesSorted = txnData
    ? [...txnData.branches].sort((a, b) => {
        const netA =
          parseFloat(a.total_receipts) - parseFloat(a.total_payments);
        const netB =
          parseFloat(b.total_receipts) - parseFloat(b.total_payments);
        return netB - netA;
      })
    : [];

  return (
    <PageLayout logo={auth?.client?.logo} clientName={auth?.client?.name}>
      <div className="p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header: icon + title horizontally aligned, date below */}
          <div className="mb-2">
            <div className="flex items-center">
              <ChartBarIcon
                className="w-7 h-7 mr-2"
                style={{ color: "#069494ff" }}
              />
              <h1 className="text-2xl font-bold" style={{ color: "#069494ff" }}>
                Daily Sales Overview
              </h1>
            </div>
            {data && (
              <p className="text-sm text-gray-500 mt-1">
                For{" "}
                <span className="font-semibold">
                  {new Date(data.date).toLocaleDateString()}
                </span>
              </p>
            )}
          </div>

          {loading && (
            <div className="text-center py-10">
              <p className="text-gray-500">Loading daily sales data...</p>
            </div>
          )}

          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg"
              role="alert"
            >
              <strong className="font-bold">Error:</strong>
              <span className="block sm:inline ml-2">{error}</span>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Totals Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* Total Sales All Branches */}
                <div
                  className="text-white p-6 rounded-xl shadow-lg"
                  style={{ backgroundColor: "#069494ff" }}
                >
                  <div className="flex items-center">
                    <BanknotesIcon
                      className="w-10 h-10 mr-4"
                      style={{ color: "#ffce1bff" }}
                    />
                    <div>
                      <p
                        className="text-lg font-semibold"
                        style={{ color: "#ffce1bff" }}
                      >
                        Total Sales Today
                      </p>
                      <p
                        className="text-3xl font-bold"
                        style={{ color: "#ffce1bff" }}
                      >
                        {`${salesCurrencyName} ${formatNumber(totalSalesAll)}`}
                      </p>
                      <p className="text-xs text-white/80 mt-1">
                        Across {data.branches.length} active branches
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total Return Sales */}
                <div
                  className="text-white p-6 rounded-xl shadow-lg"
                  style={{ backgroundColor: "#ffce1bff" }}
                >
                  <div className="flex items-center">
                    <BanknotesIcon
                      className="w-10 h-10 mr-4"
                      style={{ color: "#069494ff" }}
                    />
                    <div>
                      <p
                        className="text-lg font-semibold"
                        style={{ color: "#069494ff" }}
                      >
                        Return Sales Today
                      </p>
                      <p
                        className="text-3xl font-bold"
                        style={{ color: "#069494ff" }}
                      >
                        {`${salesCurrencyName} ${formatNumber(totalReturnSalesAll)}`}
                      </p>
                      <p className="text-xs text-gray-700 mt-1">
                        Total value of approved returns
                      </p>
                    </div>
                  </div>
                </div>

                {/* Net Sales */}
                <div
                  className="text-white p-6 rounded-xl shadow-lg"
                  style={{ backgroundColor: "#b7410eff" }}
                >
                  <div className="flex items-center">
                    <BanknotesIcon className="w-10 h-10 mr-4" />
                    <div>
                      <p className="text-lg font-semibold">Net Sales Today</p>
                      <p className="text-3xl font-bold">
                        {`${salesCurrencyName} ${formatNumber(netSalesAll)}`}
                      </p>
                      <p className="text-xs text-white/80 mt-1">
                        Sales minus return sales
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Branch Breakdown */}
              <div className="space-y-4 md:space-y-0">
                {/* Mobile cards */}
                <div className="grid grid-cols-1 gap-4 md:hidden">
                  {sortedBranches.map((branch) => {
                    const total = parseFloat(branch.total_sales);
                    const totalReturns = parseFloat(
                      (branch as any).total_return_sales ?? "0",
                    );
                    const net = total - totalReturns;
                    const contribution =
                      netSalesAll > 0 ? (net / netSalesAll) * 100 : 0;
                    return (
                      <div
                        key={branch.branch_name}
                        className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-3 border-l-4"
                        style={{ borderColor: "#069494ff" }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <BuildingStorefrontIcon className="w-5 h-5 text-gray-400 mr-2" />
                            <span className="text-sm font-semibold text-gray-900">
                              {branch.branch_name}
                            </span>
                          </div>
                          <span
                            className="text-xs px-2 py-1 rounded-full text-white"
                            style={{ backgroundColor: "#069494ff" }}
                          >
                            {contribution.toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex justify-between items-end text-sm text-gray-700 gap-4">
                          <div className="flex-1">
                            <p className="text-xs uppercase text-gray-500 mb-1">
                              Sales Today
                            </p>
                            <p className="font-semibold">
                              {`${salesCurrencySymbol} ${formatNumber(total)}`}
                            </p>
                          </div>
                          <div className="flex-1 text-right">
                            <p className="text-xs uppercase text-gray-500 mb-1">
                              Return Sales
                            </p>
                            <p className="font-semibold">
                              {`${salesCurrencySymbol} ${formatNumber(totalReturns)}`}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 flex justify-between text-xs text-gray-600">
                          <span className="uppercase">Net</span>
                          <span className="font-semibold text-gray-800">
                            {`${salesCurrencySymbol} ${formatNumber(net)}`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Branch
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Sales Today
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Return Sales
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Net Sales
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Net Contribution
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedBranches.map((branch) => {
                        const total = parseFloat(branch.total_sales);
                        const totalReturns = parseFloat(
                          (branch as any).total_return_sales ?? "0",
                        );
                        const net = total - totalReturns;
                        const contribution =
                          netSalesAll > 0 ? (net / netSalesAll) * 100 : 0;
                        return (
                          <tr
                            key={branch.branch_name}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <BuildingStorefrontIcon className="w-5 h-5 text-gray-400 mr-3" />
                                <div className="text-sm font-medium text-gray-900">
                                  {branch.branch_name}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                              {`${salesCurrencySymbol} ${formatNumber(total)}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                              {`${salesCurrencySymbol} ${formatNumber(totalReturns)}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                              {`${salesCurrencySymbol} ${formatNumber(net)}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                              {contribution.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Transactions Breakdown */}
              {txnData && (
                <div className="mt-10">
                  <div className="mb-4">
                    <div className="flex items-center">
                      <ArrowsRightLeftIcon
                        className="w-7 h-7 mr-2"
                        style={{ color: "#b7410eff" }}
                      />
                      <h2
                        className="text-2xl font-bold"
                        style={{ color: "#b7410eff" }}
                      >
                        Daily Transactions Overview
                      </h2>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      For{" "}
                      <span className="font-semibold">
                        {new Date(txnData.date).toLocaleDateString()}
                      </span>
                    </p>
                  </div>

                  {/* Totals cards for receipts/payments/net cash */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div
                      className="text-white p-6 rounded-xl shadow-lg"
                      style={{ backgroundColor: "#069494ff" }}
                    >
                      <div className="flex items-center">
                        <BanknotesIcon
                          className="w-10 h-10 mr-4"
                          style={{ color: "#ffce1bff" }}
                        />
                        <div>
                          <p
                            className="text-lg font-semibold"
                            style={{ color: "#ffce1bff" }}
                          >
                            Total Receipts
                          </p>
                          <p
                            className="text-3xl font-bold"
                            style={{ color: "#ffce1bff" }}
                          >
                            {`${txnCurrencyName} ${formatNumber(totalReceiptsAll)}`}
                          </p>
                          <p className="text-xs text-white/80 mt-1">
                            All branches, today
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      className="text-white p-6 rounded-xl shadow-lg"
                      style={{ backgroundColor: "#ffce1bff" }}
                    >
                      <div className="flex items-center">
                        <BanknotesIcon
                          className="w-10 h-10 mr-4"
                          style={{ color: "#069494ff" }}
                        />
                        <div>
                          <p
                            className="text-lg font-semibold"
                            style={{ color: "#069494ff" }}
                          >
                            Total Payments
                          </p>
                          <p
                            className="text-3xl font-bold"
                            style={{ color: "#069494ff" }}
                          >
                            {`${txnCurrencyName} ${formatNumber(totalPaymentsAll)}`}
                          </p>
                          <p className="text-xs text-gray-700 mt-1">
                            Refunds/Outflows today
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      className="text-white p-6 rounded-xl shadow-lg"
                      style={{ backgroundColor: "#b7410eff" }}
                    >
                      <div className="flex items-center">
                        <BanknotesIcon className="w-10 h-10 mr-4" />
                        <div>
                          <p className="text-lg font-semibold">
                            Net Cash Movement
                          </p>
                          <p className="text-3xl font-bold">
                            {`${txnCurrencyName} ${formatNumber(netCashAll)}`}
                          </p>
                          <p className="text-xs text-white/80 mt-1">
                            Receipts minus payments
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Branch-level transactions cards (mobile) */}
                  <div className="grid grid-cols-1 gap-4 md:hidden mb-6">
                    {txnBranchesSorted.map((branch) => {
                      const receipts = parseFloat(branch.total_receipts);
                      const payments = parseFloat(branch.total_payments);
                      const net = receipts - payments;
                      return (
                        <div
                          key={branch.branch_name}
                          className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-3 border-l-4"
                          style={{ borderColor: "#069494ff" }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <BuildingStorefrontIcon className="w-5 h-5 text-gray-400 mr-2" />
                              <span className="text-sm font-semibold text-gray-900">
                                {branch.branch_name}
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between items-end text-sm text-gray-700 gap-4">
                            <div className="flex-1">
                              <p className="text-xs uppercase text-gray-500 mb-1">
                                Receipts
                              </p>
                              <p className="font-semibold">
                                {`${txnCurrencySymbol} ${formatNumber(receipts)}`}
                              </p>
                            </div>
                            <div className="flex-1 text-right">
                              <p className="text-xs uppercase text-gray-500 mb-1">
                                Payments
                              </p>
                              <p className="font-semibold">
                                {`${txnCurrencySymbol} ${formatNumber(payments)}`}
                              </p>
                            </div>
                          </div>
                          <div className="mt-2 flex justify-between text-xs text-gray-600">
                            <span className="uppercase">Net Cash</span>
                            <span className="font-semibold text-gray-800">
                              {`${txnCurrencySymbol} ${formatNumber(net)}`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Branch-level transactions table (desktop) */}
                  <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Branch
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Receipts
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Payments
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Net Cash
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {txnBranchesSorted.map((branch) => {
                          const receipts = parseFloat(branch.total_receipts);
                          const payments = parseFloat(branch.total_payments);
                          const net = receipts - payments;
                          return (
                            <tr
                              key={branch.branch_name}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <BuildingStorefrontIcon className="w-5 h-5 text-gray-400 mr-3" />
                                  <div className="text-sm font-medium text-gray-900">
                                    {branch.branch_name}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                                {`${txnCurrencySymbol} ${formatNumber(receipts)}`}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                                {`${txnCurrencySymbol} ${formatNumber(payments)}`}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                                {`${txnCurrencySymbol} ${formatNumber(net)}`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
